import React from 'react';
import { CoverageMonthData } from './StrategiesUtils';
import { IMassUnitObject } from '../../../sp-types/massUnitType';
import type { TimeGranularity } from '../../../utils/timeGranularity';
import { GRANULARITY_LABELS } from '../../../utils/timeGranularity';

interface Props {
  coverageData: CoverageMonthData[];
  matPrice: number;
  unit: IMassUnitObject;
  selectedMonthIndex?: number;
  timeGranularity?: TimeGranularity;
}

export const CoverageMonthDetail: React.FC<Props> = ({ coverageData, matPrice, selectedMonthIndex = 0, timeGranularity = 'Month' }) => {
  const isAggregated = timeGranularity !== 'Month';
  // +1 to skip on-hand bar at index 0
  const dataIndex = selectedMonthIndex + 1;
  const d = coverageData?.[dataIndex];

  if (!d) {
    return (
      <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md p-4">
        <div className="text-gray-400 text-sm text-center py-8">No data available</div>
      </div>
    );
  }

  const buffer = d.emergencyStock || 0;
  const expectedDemand = d.expectedDemand || 0;
  const currCover = d.currCover || 0;
  const reqCover = d.reqCover || 0;
  const strategyPercent = d.covRatio || 0;
  const covPercentage = d.covPercentage || 0;
  const physical = d.stock + d.POs;
  const financial = d.financialPositions;
  const totalNeed = expectedDemand + buffer;

  const isGood = covPercentage >= 100;
  const shortage = isGood ? 0 : reqCover - currCover;
  const maxVal = Math.max(currCover, totalNeed, reqCover) * 1.2 || 1;

  return (
    <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md p-4 max-h-full overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span style={{ color: 'rgba(59, 130, 246, 0.5)', fontSize: '0.65em' }}>&#9654;</span>
          <span className="text-base font-semibold text-gray-900">{d.month}</span>
          <span className="text-sm text-gray-400">{isAggregated ? `${GRANULARITY_LABELS[timeGranularity]} ` : ''}Coverage</span>
        </div>
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
          isGood ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {isGood ? '✓' : '⚠'} {covPercentage}%
        </span>
      </div>
      {isAggregated && (
        <div className="text-xs text-gray-400 mb-3 leading-tight">
          Flows are summed across all months in this {GRANULARITY_LABELS[timeGranularity].toLowerCase()} period. Coverage % is averaged.
        </div>
      )}

      {/* Comparison Bars */}
      <div className="space-y-3 mb-4">
        <div>
          <div className="text-xs font-medium text-gray-500 mb-1.5">Coverage</div>
          <div className="relative h-5 rounded-md bg-gray-100 overflow-hidden">
            <div className="absolute h-full rounded-l-md" style={{ width: `${(physical / maxVal) * 100}%`, backgroundColor: '#10B981' }} />
            <div className="absolute h-full" style={{ left: `${(physical / maxVal) * 100}%`, width: `${(financial / maxVal) * 100}%`, backgroundColor: '#93C5FD' }} />
            <div className="absolute h-full w-0.5 z-10" style={{ left: `${(reqCover / maxVal) * 100}%`, backgroundColor: '#EF4444' }} />
          </div>
        </div>

        <div>
          <div className="text-xs font-medium text-gray-500 mb-1.5">Demand + Buffer</div>
          <div className="relative h-5 rounded-md bg-gray-100 overflow-hidden">
            <div className="absolute h-full" style={{ width: `${(buffer / maxVal) * 100}%`, background: 'repeating-linear-gradient(45deg, #FCA5A5, #FCA5A5 6px, #FEE2E2 6px, #FEE2E2 12px)' }} />
            <div className="absolute h-full" style={{ width: `${(expectedDemand / maxVal) * 100}%`, left: `${(buffer / maxVal) * 100}%`, backgroundColor: '#A3A3A3' }} />
            <div className="absolute h-full w-0.5 z-10" style={{ left: `${(reqCover / maxVal) * 100}%`, backgroundColor: '#EF4444' }} />
          </div>
        </div>
      </div>

      {/* Action */}
      <div className={`p-2.5 rounded-lg text-sm mb-4 ${isGood ? 'bg-green-50' : 'bg-red-50'}`}>
        {isGood ? (
          <span className="text-green-700 font-medium">✓ Fully covered for {d.month}</span>
        ) : (
          <div>
            <div className="text-red-700 font-medium">⚠ Undercovered by {Math.round(shortage).toLocaleString()} mt</div>
            <div className="text-red-600 text-xs mt-1">Consider hedging to close the gap</div>
          </div>
        )}
      </div>

      {/* Breakdown */}
      <div className="space-y-0 divide-y divide-gray-100">
        <Row color="#10B981" label="Physical (Carryover + POs)" qty={physical} price={matPrice} />
        <Row color="#93C5FD" label="Financial (Hedges)" qty={financial} price={matPrice} />
        <Row stripe="#FCA5A5" label="Buffer" qty={buffer} price={matPrice} />
        <Row color="#A3A3A3" label="Expected Demand" qty={expectedDemand} price={matPrice} />
        <div className="pt-2">
          <Row color="#EF4444" label="Required Coverage" qty={reqCover} price={matPrice} bold />
          <div className="ml-5 text-xs text-gray-400 pb-1">{strategyPercent}% × (Demand + Buffer)</div>
        </div>
      </div>

    </div>
  );
};

const Row: React.FC<{
  color?: string;
  stripe?: string;
  label: string;
  qty: number;
  price: number;
  bold?: boolean;
}> = ({ color, stripe, label, qty, price, bold }) => (
  <div className="flex items-center justify-between py-1.5">
    <div className="flex items-center gap-2 min-w-0">
      <div
        className="w-3 h-3 rounded-sm flex-shrink-0"
        style={stripe
          ? { background: `repeating-linear-gradient(45deg, ${stripe}, ${stripe} 2px, ${stripe}44 2px, ${stripe}44 4px)` }
          : { backgroundColor: color }
        }
      />
      <span className={`text-sm truncate ${bold ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{label}</span>
    </div>
    <div className="flex items-center gap-3 flex-shrink-0 ml-2">
      <span className={`text-sm tabular-nums ${bold ? 'font-bold text-gray-900' : 'font-semibold text-gray-900'}`}>
        {Math.round(qty).toLocaleString()} mt
      </span>
      <span className="text-xs text-gray-400 w-16 text-right tabular-nums">
        ${Math.round(qty * price).toLocaleString()}
      </span>
    </div>
  </div>
);
